import { useEffect, useState, type FormEvent } from "react";
import { brand } from "../config/brand";
import { LiveDisabledDisclaimer, RecoveryCodesOnce, TotpQr } from "../components/MfaUi";
import {
  beginRecovery,
  beginReenroll,
  confirmReenroll,
  loginWithPassword,
  recoveryVerify,
  verifyTotpMfa,
  type AuthSessionResult,
  type SessionInfo,
  type SessionUser,
} from "../lib/auth";

type LoginStep =
  | "credentials"
  | "totp"
  | "recovery"
  | "reenroll"
  | "recovery-codes";

export function LoginPage({
  onNavigate,
  onAuthenticated,
}: {
  onNavigate: (path: string) => void;
  onAuthenticated: (session: {
    user: SessionUser;
    session: SessionInfo;
    csrfToken: string;
  }) => void;
}) {
  const [step, setStep] = useState<LoginStep>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [pendingSession, setPendingSession] = useState<AuthSessionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginWithPassword(email, password);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      if ("mfaRequired" in result && result.mfaRequired === true) {
        setTotpCode("");
        setStep("totp");
        return;
      }
      onAuthenticated({
        user: result.user,
        session: result.session,
        csrfToken: result.csrfToken,
      });
    } catch {
      setError("Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onTotpSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await verifyTotpMfa(totpCode);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      onAuthenticated({
        user: result.user,
        session: result.session,
        csrfToken: result.csrfToken,
      });
    } catch {
      setError("Verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onUseRecovery() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await beginRecovery();
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setRecoveryCode("");
      setStep("recovery");
    } catch {
      setError("Recovery could not be started");
    } finally {
      setSubmitting(false);
    }
  }

  async function onRecoverySubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const verified = await recoveryVerify(recoveryCode);
      if (!verified.ok) {
        setError(verified.message);
        return;
      }
      const reenroll = await beginReenroll();
      if (!reenroll.ok) {
        setError(reenroll.message);
        return;
      }
      setOtpauthUri(reenroll.otpauthUri);
      setTotpCode("");
      setStep("reenroll");
    } catch {
      setError("Recovery verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onReenrollSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await confirmReenroll(totpCode);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setOtpauthUri(null);
      setPendingSession({
        user: result.user,
        session: result.session,
        csrfToken: result.csrfToken,
      });
      setRecoveryCodes(result.recoveryCodes);
      setStep("recovery-codes");
    } catch {
      setError("Re-enrollment confirmation failed");
    } finally {
      setSubmitting(false);
    }
  }

  function onAcknowledgeRecovery() {
    const session = pendingSession;
    setRecoveryCodes(null);
    setPendingSession(null);
    if (session) onAuthenticated(session);
  }

  const title =
    step === "credentials"
      ? "Sign in"
      : step === "totp"
        ? "Authenticator"
        : step === "recovery"
          ? "Recovery code"
          : step === "reenroll"
            ? "Re-enroll authenticator"
            : "Recovery codes";

  return (
    <div className="login-shell">
      <form
        className="login-card"
        onSubmit={
          step === "credentials"
            ? onPasswordSubmit
            : step === "totp"
              ? onTotpSubmit
              : step === "recovery"
                ? onRecoverySubmit
                : step === "reenroll"
                  ? onReenrollSubmit
                  : (e) => e.preventDefault()
        }
        style={step === "recovery-codes" ? { width: "min(480px, 100%)" } : undefined}
      >
        <div className="sidebar-mark" aria-hidden="true" style={{ width: 42, height: 42 }}>
          {brand.logoText}
        </div>
        <div>
          <h1>{title}</h1>
          <p className="subtitle">{brand.productName}</p>
        </div>
        <LiveDisabledDisclaimer />

        {step === "credentials" ? (
          <>
            <div className="field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                required
              />
            </div>
            {error ? (
              <div className="login-disclaimer" role="alert">
                {error}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Signing in…" : "Continue"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => onNavigate("/status")}>
              Public status
            </button>
          </>
        ) : null}

        {step === "totp" ? (
          <>
            <div className="field">
              <label htmlFor="login-totp">Authenticator code</label>
              <input
                id="login-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={8}
                value={totpCode}
                onChange={(ev) => setTotpCode(ev.target.value)}
                required
              />
            </div>
            {error ? (
              <div className="login-disclaimer" role="alert">
                {error}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={submitting}
              onClick={() => void onUseRecovery()}
            >
              Use a recovery code
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={submitting}
              onClick={() => {
                setStep("credentials");
                setTotpCode("");
                setError(null);
              }}
            >
              Back
            </button>
          </>
        ) : null}

        {step === "recovery" ? (
          <>
            <div className="field">
              <label htmlFor="recovery-code">Recovery code</label>
              <input
                id="recovery-code"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={recoveryCode}
                onChange={(ev) => setRecoveryCode(ev.target.value)}
                required
              />
            </div>
            {error ? (
              <div className="login-disclaimer" role="alert">
                {error}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Verifying…" : "Continue"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={submitting}
              onClick={() => {
                setStep("totp");
                setError(null);
              }}
            >
              Back to authenticator
            </button>
          </>
        ) : null}

        {step === "reenroll" && otpauthUri ? (
          <>
            <div className="login-disclaimer" role="note">
              Recovery succeeded. Enroll a new authenticator before accessing the dashboard.
            </div>
            <TotpQr otpauthUri={otpauthUri} />
            <div className="field">
              <label htmlFor="reenroll-totp">New authenticator code</label>
              <input
                id="reenroll-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={8}
                value={totpCode}
                onChange={(ev) => setTotpCode(ev.target.value)}
                required
              />
            </div>
            {error ? (
              <div className="login-disclaimer" role="alert">
                {error}
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Confirming…" : "Confirm new TOTP"}
            </button>
          </>
        ) : null}

        {step === "recovery-codes" && recoveryCodes ? (
          <RecoveryCodesOnce codes={recoveryCodes} onContinue={onAcknowledgeRecovery} />
        ) : null}
      </form>
    </div>
  );
}

export function UnauthorizedPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  useEffect(() => {
    // Fail closed — no preview bypass.
  }, []);
  return (
    <div className="login-shell">
      <div className="login-card">
        <h1>Authentication required</h1>
        <p className="subtitle">Sign in to access the control plane dashboard.</p>
        <div className="login-disclaimer" role="note">
          Live execution remains disabled. Preview demo bypass has been removed. An MFA-verified
          session is required.
        </div>
        <button type="button" className="btn btn-primary" onClick={() => onNavigate("/login")}>
          Go to sign in
        </button>
      </div>
    </div>
  );
}

export function SessionLoadingPage() {
  return (
    <div className="login-shell">
      <div className="login-card">
        <h1>Loading session…</h1>
        <p className="subtitle">{brand.productName}</p>
      </div>
    </div>
  );
}
